import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import {
  engineReady,
  ENGINES,
  useAiSettings,
  useEngines,
  type EngineId,
  type EngineStatus,
} from "./ai";

const TAG = {
  free: ["Gratis", "var(--good)"],
  paid: ["De pago", "var(--warning,#d4a017)"],
  plan: ["Tu plan", "var(--ai)"],
} as const;
const hora = (s: string) =>
  new Date(s).toLocaleString("es-MX", {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
const n = (x: number) => x.toLocaleString("es-MX");

/** Uso de hoy de un servicio: consultas y cupo si el servicio lo informa. */
const usage = (s?: EngineStatus) => {
  if (!s) return "";
  const parts = [
    `Hoy: ${n(s.calls_today)} consulta${s.calls_today === 1 ? "" : "s"}`,
  ];
  if (s.day_limit)
    return `Hoy: ${n(s.calls_today)} de ${n(s.day_limit)} consultas${s.calls_today >= s.day_limit ? " (tope del día: pasa a Groq)" : ""} · máximo 5 por minuto (espera su turno)`;
  if (s.remaining != null)
    parts.push(
      `quedan ${n(s.remaining)}${s.limit_n ? ` de ${n(s.limit_n)}` : ""}`,
    );
  if (s.exhausted_until)
    parts.push(`sin cupo hasta ${hora(s.exhausted_until)}`);
  return parts.join(" · ");
};

/** IA y ajustes → Motor de IA e investigación nocturna (PRD G.1, G.3). */
export const AiSection = () => {
  const { data: cfg } = useAiSettings();
  const { data: st } = useEngines();
  const [v, setV] = useState<{
    engine: EngineId;
    second: string;
    nightly: boolean;
    count: number;
    hour: string;
  } | null>(null);
  const notify = useNotify();
  const qc = useQueryClient();
  useEffect(() => {
    if (cfg)
      setV({
        engine: cfg.ai_engine,
        second: cfg.ai_fallback?.[1] ?? "groq",
        nightly: cfg.nightly_research,
        count: cfg.nightly_count,
        hour: cfg.nightly_hour.slice(0, 5),
      });
  }, [cfg]);
  if (!v) return null;
  const s = (id: string) => st?.find((x) => x.service === id);
  const queued = (st ?? []).reduce(
    (a, x) => (x.service === "claude_plan" ? a : a + x.queued),
    0,
  );
  const save = async (patch: Partial<typeof v>) => {
    const next = { ...v, ...patch };
    setV(next);
    const { data, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({
        ai_engine: next.engine,
        ai_fallback: [
          "gemini",
          next.second,
          next.second === "groq" ? "openrouter" : "groq",
        ],
        nightly_research: next.nightly,
        nightly_count: Math.min(10, Math.max(1, next.count || 1)),
        nightly_hour: next.hour,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1)
      .select("id");
    if (error || !data?.length)
      return notify("Solo un administrador puede cambiar esto", {
        type: "error",
      });
    qc.invalidateQueries({ queryKey: ["crm_settings"] });
    notify("Guardado", { type: "success" });
  };

  return (
    <Card id="ia" className="gap-3 p-4">
      <h2 className="flex items-center gap-2 text-base font-extrabold">
        <Sparkles className="size-5 text-[var(--ai)]" />
        Motor de IA
      </h2>
      <p className="text-sm text-muted-foreground">
        El que elijas aquí se usa en todo el CRM; en la ficha de cada empresa
        puedes elegir otro solo para esa investigación. Si un motor se queda sin
        cupo, pasa al siguiente gratis; si todos se acaban, la investigación
        espera en la cola y sigue cuando se renueve el cupo (normalmente al día
        siguiente).
      </p>
      <div className="grid gap-2 lg:grid-cols-2">
        {ENGINES.map((e) => {
          const why = engineReady(e.id, st);
          const u = e.id === "auto" ? undefined : s(e.id);
          const [tag, color] = TAG[e.tag];
          return (
            <label
              key={e.id}
              className={cn(
                "flex cursor-pointer gap-2.5 rounded-lg border p-3",
                v.engine === e.id && "border-primary bg-accent/40",
                why && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="radio"
                name="engine"
                className="mt-1 size-4 accent-[var(--primary)]"
                checked={v.engine === e.id}
                disabled={!!why}
                onChange={() => save({ engine: e.id })}
              />
              <span className="grid gap-1 text-sm">
                <span className="flex flex-wrap items-center gap-2 font-bold">
                  {e.name}
                  <span
                    className="rounded-full border px-1.5 text-[10px] font-bold"
                    style={{ borderColor: color, color }}
                  >
                    {tag}
                  </span>
                </span>
                <span className="text-muted-foreground">{e.desc}</span>
                <span className="text-xs text-muted-foreground">
                  <b>Límite:</b> {e.lim}
                </span>
                {why ? (
                  <span className="text-xs font-semibold text-[var(--critical)]">
                    {why}
                  </span>
                ) : e.id === "auto" ? (
                  <span className="text-xs">
                    {["gemini", "groq", "openrouter"]
                      .map(
                        (x) =>
                          `${ENGINES.find((y) => y.id === x)?.short}: ${s(x)?.calls_today ?? 0}`,
                      )
                      .join(" · ")}{" "}
                    consultas hoy
                  </span>
                ) : e.id === "claude_plan" ? (
                  <span className="text-xs">
                    En cola para Claude: {s("claude_plan")?.queued ?? 0} ·
                    investigadas hoy: {s("claude_plan")?.calls_today ?? 0}
                  </span>
                ) : (
                  <span className="text-xs">{usage(u)}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      <label className="flex flex-wrap items-center gap-2 text-sm">
        <b>Orden del Automático:</b> Gemini →
        <select
          className="h-8 rounded-md border bg-card px-2"
          value={v.second}
          onChange={(e) => save({ second: e.target.value })}
          aria-label="Segundo motor del Automático"
        >
          <option value="groq">Groq</option>
          <option value="openrouter">OpenRouter</option>
        </select>
        → {v.second === "groq" ? "OpenRouter" : "Groq"}
        <span className="text-xs text-muted-foreground">
          (en la prueba del 25/09 Groq dio mejor expediente y fue 5 veces más
          rápido)
        </span>
      </label>
      {queued > 0 && (
        <p className="text-sm text-muted-foreground">
          En cola ahora: {queued}.
        </p>
      )}

      <h3 className="mt-2 font-bold">Cupos del mes</h3>
      <MonthlyQuota st={st} />

      <h3 className="mt-2 font-bold">Investigación nocturna</h3>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-[var(--primary)]"
          checked={v.nightly}
          onChange={(e) => save({ nightly: e.target.checked })}
          aria-label="Investigación nocturna"
        />
        Cada noche investigar las
        <input
          type="number"
          min={1}
          max={10}
          className="h-8 w-16 rounded-md border bg-card px-2"
          value={v.count}
          onChange={(e) => setV({ ...v, count: Number(e.target.value) })}
          onBlur={() => save({})}
          aria-label="Cuántas empresas"
        />
        empresas más calientes sin investigar, a las
        <input
          type="time"
          className="h-8 rounded-md border bg-card px-2"
          value={v.hour}
          onChange={(e) => setV({ ...v, hour: e.target.value })}
          onBlur={() => save({})}
          aria-label="Hora"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Máximo 10 por noche, solo con Groq y OpenRouter: Gemini se guarda para
        las que pidas a mano. Empieza por las de mayor termómetro sin
        investigar. Si se acaba el cupo, se detiene y sigue la noche siguiente.
        No contacta a nadie ni las pasa al CRM.
      </p>
      <NightlySummary />
    </Card>
  );
};

/** Propuesta de pesos de la IA al llegar a 30 cerradas (PRD E.6): Ian aprueba o descarta. */
export const CalibrationProposal = () => {
  const { data: cfg } = useAiSettings();
  const notify = useNotify();
  const qc = useQueryClient();
  const p = cfg?.thermo_proposal;
  if (!p) return null;
  const apply = async (ok: boolean) => {
    const { error } = await getSupabaseClient().rpc("thermo_proposal_apply", {
      p_approve: ok,
    });
    if (error) return notify(error.message, { type: "error" });
    notify(
      ok
        ? "Pesos nuevos aplicados: el termómetro se recalcula en unos minutos"
        : "Propuesta descartada",
      { type: "success" },
    );
    qc.invalidateQueries({ queryKey: ["crm_settings"] });
  };
  const L: Record<string, string> = {
    fit: "Encaje",
    cont: "Contactabilidad",
    size: "Tamaño",
    sig: "Señales",
    int: "Interacción",
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[var(--ai)] p-3">
      <b>
        La IA propone pesos nuevos ({p.cerradas} cerradas, {p.motor})
      </b>
      <p>{p.explicacion}</p>
      <p className="font-semibold">
        {Object.entries(p.pesos)
          .map(([k, x]) => `${L[k] ?? k} ${x}`)
          .join(" · ")}
      </p>
      {p.prob && Object.keys(p.prob).length > 0 && (
        <p className="text-muted-foreground">
          Probabilidad por etapa:{" "}
          {Object.entries(p.prob)
            .map(([k, x]) => `${k} ${Math.round(Number(x) * 100)}%`)
            .join(" · ")}
        </p>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => apply(true)}>
          Aprobar y aplicar
        </Button>
        <Button size="sm" variant="outline" onClick={() => apply(false)}>
          Descartar
        </Button>
      </div>
    </div>
  );
};

const QUOTAS: [string, string][] = [
  ["tavily", "Tavily (buscador)"],
  ["gemini_busqueda", "Búsqueda de Google dentro de Gemini"],
  ["serpapi", "SerpApi (reseñas, Bloque H)"],
];

/** Contadores mensuales: al llegar al 90 % el CRM deja de usar el servicio y avisa. */
export const MonthlyQuota = ({ st }: { st?: EngineStatus[] }) => (
  <div className="grid gap-2 text-sm">
    {QUOTAS.map(([id, label]) => {
      const q = st?.find((x) => x.service === id);
      if (!q?.month_limit) return null;
      const pct = Math.min(100, (q.month_used / q.month_limit) * 100);
      return (
        <div key={id} className="grid gap-1">
          <div className="flex flex-wrap justify-between gap-2">
            <span>{label}</span>
            <span
              className={cn(q.blocked && "font-bold text-[var(--critical)]")}
            >
              {n(q.month_used)} de {n(q.month_limit)} este mes
              {q.blocked && " · en pausa (llegó al 90 %)"}
            </span>
          </div>
          <span className="h-2 overflow-hidden rounded-full bg-secondary">
            <i
              className="block h-full rounded-full"
              style={{
                width: `${pct}%`,
                background: q.blocked
                  ? "var(--critical)"
                  : pct >= 75
                    ? "var(--warning,#d4a017)"
                    : "var(--good)",
              }}
            />
          </span>
        </div>
      );
    })}
    <p className="text-xs text-muted-foreground">
      Tavily: tu cuenta (plan Researcher) tiene 1,500 al mes. SerpApi: 250 al
      mes. Google: 5,000 búsquedas al mes compartidas por los modelos Gemini 3.
      Al llegar al 90 % el CRM deja de usarlos hasta el mes siguiente.
    </p>
  </div>
);

type Night = {
  noche: string | null;
  investigadas: number;
  en_cola: number;
  errores: number;
  por_motor: Record<string, number>;
};
export const useNightly = () =>
  useQuery({
    queryKey: ["nightly_summary"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await getSupabaseClient().rpc("nightly_summary");
      return data as Night | null;
    },
  });
export const nightlyText = (x?: Night | null) =>
  x?.noche
    ? `Anoche la IA investigó ${x.investigadas} empresa${x.investigadas === 1 ? "" : "s"}${
        Object.keys(x.por_motor).length
          ? ` (${Object.entries(x.por_motor)
              .map(([k, v]) => `${k}: ${v}`)
              .join(", ")})`
          : ""
      }${x.en_cola ? ` · ${x.en_cola} quedaron para la próxima noche` : ""}${
        x.errores
          ? ` · ${x.errores} fallaron (vuelven a la fila la próxima noche)`
          : ""
      }.`
    : null;

const NightlySummary = () => {
  const { data } = useNightly();
  const t = nightlyText(data);
  return t ? <p className="text-sm font-semibold">{t}</p> : null;
};

/** IA y ajustes → correo diario de investigaciones (PRD M.5). Apagado por defecto. */
export const ResearchEmailSection = () => {
  const notify = useNotify();
  const qc = useQueryClient();
  const { data: enabled } = useQuery({
    queryKey: ["crm_settings", "research_email"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("crm_settings")
        .select("research_email")
        .eq("id", 1)
        .single();
      if (error) throw error;
      return !!(data?.research_email as { enabled?: boolean })?.enabled;
    },
  });
  const toggle = async (checked: boolean) => {
    const { data, error } = await getSupabaseClient()
      .from("crm_settings")
      .update({ research_email: { enabled: checked } })
      .eq("id", 1)
      .select("id");
    if (error || !data?.length)
      return notify("Solo un administrador puede cambiar esto", {
        type: "error",
      });
    qc.invalidateQueries({ queryKey: ["crm_settings", "research_email"] });
    notify("Guardado", { type: "success" });
  };
  return (
    <Card className="gap-2 p-4">
      <h2 className="text-base font-extrabold">
        Correo diario de investigaciones
      </h2>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={!!enabled}
          onChange={(e) => toggle(e.target.checked)}
        />
        Mandarme cada mañana (7:00, hora de México) las 5 más calientes de
        anoche
      </label>
      <p className="text-xs text-muted-foreground">
        Necesita que el correo SMTP ya esté configurado (Correo con tu Gmail);
        si todavía no lo está, esto se queda listo pero no manda nada.
      </p>
    </Card>
  );
};
